// src/components/auth/Login.tsx
import React, { useState, useRef } from "react";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useUser } from "../../contexts/UserContext";
import HCaptcha from "@hcaptcha/react-hcaptcha";

// Styled components remain unchanged...
const Container = styled.div`
  max-width: 450px;
  margin: 2rem auto;
  background-color: var(--white);
  border-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 2rem;
  width: 90%;
`;
const Title = styled.h1`
  color: var(--primary, #7e22ce);
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;
const Subtitle = styled.p`
  color: var(--text-light, #94a3b8);
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1rem;
`;
const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;
const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;
const InputContainer = styled.div`
  position: relative;
`;
const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-light, #94a3b8);
`;
const PasswordToggle = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-light, #94a3b8);
  cursor: pointer;
  background: none;
  border: none;
`;
const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.3s;
  &:focus {
    outline: none;
    border-color: var(--primary-light, #a855f7);
  }
`;
const Button = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: var(--primary, #7e22ce);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background-color 0.3s;
  &:hover {
    background-color: var(--primary-dark, #6b21a8);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;
const BottomText = styled.p`
  margin-top: 1.5rem;
  text-align: center;
  color: var(--text-light, #94a3b8);
`;
const StyledLink = styled(Link)`
  color: var(--primary, #7e22ce);
  text-decoration: none;
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
`;
const ErrorText = styled.p`
  color: #ef4444;
  font-size: 0.875rem;
  text-align: center;
  margin-top: 0.75rem;
`;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { setProfile } = useUser();
  const captchaRef = useRef<HCaptcha>(null);

  const [identifier, setIdentifier] = useState<string>(""); // Can be email or phone number
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [captchaToken, setCaptchaToken] = useState<string>("");
  const [isContentCreator, setIsContentCreator] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (!identifier || !password) {
      setError("Please enter both identifier and password");
      return;
    }

    if (!captchaToken) {
      setError("Please complete the CAPTCHA verification");
      return;
    }

    setLoading(true);

    try {
      // Check if the identifier is a phone number (Kenyan format)
      const isPhoneNumber = /^(07\d{8}|\+2547\d{8})$/.test(identifier);
      
      // Include role information in the login request
      const role = isAdmin ? 'admin' : isContentCreator ? 'content_creator' : undefined;
      
      // Call login with the correct number of arguments
      await login(identifier, password, captchaToken, role, isPhoneNumber);
      
      // Get the user from AuthContext after login
      const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
      if (!authUser || !authUser.id) {
        throw new Error("Login failed: No user data returned from server.");
      }
      setProfile(authUser);
      console.log("Login successful, user:", authUser);
      
      // Check user role from the backend response
      if (authUser.role === 'admin') {
        navigate("/admin");
      } else if (authUser.role === 'content_creator') {
        navigate("/content-creator/dashboard");
      } else if (authUser.role === 'teacher') {
        navigate("/teacher/dashboard");
      } else if (authUser.role === 'competitor') {
        navigate("/app/compete/dashboard");
      } else if (authUser.onboardingCompleted) {
        navigate("/app/dashboard");
      } else {
        navigate("/onboarding");
      }
    } catch (err: any) {
      console.error("Error during login:", err);
      // Show more specific error messages
      if (err.message.includes("already exists")) {
        setError("An account with this email already exists. Please try logging in instead.");
      } else if (err.message.includes("Invalid credentials")) {
        setError("Invalid credentials. Please try again.");
      } else if (err.message.includes("Account is temporarily locked")) {
        setError("Your account has been temporarily locked due to too many failed attempts. Please try again later.");
      } else if (err.message.includes("CAPTCHA")) {
        setError("CAPTCHA verification failed. Please try again.");
      } else if (err.message.includes("role")) {
        setError("Invalid role selected. Please check your role selection.");
      } else {
        setError(err.message || "An unexpected error occurred. Please try again.");
      }
      // Reset CAPTCHA on error
      if (captchaRef.current) {
        captchaRef.current.resetCaptcha();
      }
      setCaptchaToken("");
    } finally {
      setLoading(false);
    }
  };

  const onCaptchaChange = (token: string) => {
    setCaptchaToken(token);
  };

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  return (
    <Container>
      <Title>Welcome to Lavern Titan</Title>
      <Subtitle>Sign in to continue your learning journey</Subtitle>
      
      <form onSubmit={handleLogin}>
        <FormGroup>
          <Label htmlFor="identifier">Email or Phone Number</Label>
          <InputContainer>
            <InputIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M22 7l-10 7-10-7" />
              </svg>
            </InputIcon>
            <Input
              type="text"
              id="identifier"
              placeholder="Enter your email or phone number"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
            />
          </InputContainer>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <InputContainer>
            <InputIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </InputIcon>
            <Input
              type={showPassword ? "text" : "password"}
              id="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <PasswordToggle type="button" onClick={togglePasswordVisibility}>
              {showPassword ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </PasswordToggle>
          </InputContainer>
        </FormGroup>

        <FormGroup>
          <Label>
            <input
              type="checkbox"
              checked={isContentCreator}
              onChange={(e) => {
                setIsContentCreator(e.target.checked);
                if (e.target.checked) setIsAdmin(false);
              }}
            />
            {" "}Login as Content Creator
          </Label>
        </FormGroup>

        <FormGroup>
          <Label>
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => {
                setIsAdmin(e.target.checked);
                if (e.target.checked) setIsContentCreator(false);
              }}
            />
            {" "}Login as Admin
          </Label>
        </FormGroup>
        
        <FormGroup>
          <HCaptcha
            ref={captchaRef}
            sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY || ""}
            onVerify={onCaptchaChange}
          />
        </FormGroup>

        <Button type="submit" disabled={loading}>
          {loading ? "Kindly wait, signing ..." : "Sign In"}
        </Button>

        {error && <ErrorText>{error}</ErrorText>}

        <BottomText>
          Don't have an account? <StyledLink to="/auth/unified-registration">Sign Up</StyledLink>
        </BottomText>
      </form>
    </Container>
  );
};

export default Login;